import {
  Component,
  OnDestroy,
  OnInit,
  viewChild,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import {
  ConfirmationService,
  FilterMetadata,
  PrimeTemplate,
} from '@openng/optimus-ui/api';
import { forkJoin, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import {
  LazyLoadFilters,
  PageRequest,
  SearchContainerComponent,
} from 'src/app/core/containers/search-container/search-container.component';
import { TabService } from 'src/app/core/services/tab.service';
import { AdminBuilderService } from 'src/app/services/builder/admin-builder.service';
import { AdminApiService } from 'src/app/services/api/admin-api.service';
import { LogAndMessageService } from 'src/app/services/log-and-message.service';
import { NOTE_TYPES_TABLE_DEFINITION } from '../../definitions/data-table-definitions';
import { BusinessUnit } from 'src/app/model/admin/business-unit';
import {
  CategoryObject,
  NoteType,
  NoteTypeSortOrQuery,
} from 'src/app/model/crm/note-type';
import { NoteTypeInput } from 'src/app/model/admin/note-type-input';
import { isValidNoteTypeInput } from 'src/app/crm/functions/note-type-functions';
import { NoteTypeApiService } from 'src/app/services/api/note-type-api.service';
import { AuthApiService } from 'src/app/services/api/auth-api.service';
import { BusinessUnitFilterComponent } from 'src/app/core/components/business-unit-filter/business-unit-filter.component';
import { isNullOrUndefined, sortByProperty } from 'src/app/utils/utils';
import { CLAIMS } from 'src/app/constants/auth-constants';
import { NoteTypeTableService } from 'src/app/services/table/note-type-table.service';
import { GridLazyLoadEvent } from 'src/app/core/components/base-grid.component';
import { AuthService } from 'src/app/services/auth.service';
import { BlockUI } from '@openng/optimus-ui/blockui';
import { ProgressSpinnerComponent } from '../../../core/components/progress-spinner.component';
import { ButtonDirective } from '@openng/optimus-ui/button';
import { GeneralGridComponent } from '../../../core/components/general-grid.component';
import { Dialog } from '@openng/optimus-ui/dialog';
import { InputText } from '@openng/optimus-ui/inputtext';
import { Checkbox } from '@openng/optimus-ui/checkbox';
import { PickList } from '@openng/optimus-ui/picklist';
import { MultiSelect } from '@openng/optimus-ui/multiselect';
import { AOR } from 'src/app/model/locations/aor';
import { AorApiService } from 'src/app/services/api/aor-api.service';

@Component({
  selector: 'app-note-types-container',
  templateUrl: './note-types-container.component.html',
  styleUrls: ['./note-types-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    BlockUI,
    ProgressSpinnerComponent,
    BusinessUnitFilterComponent,
    ButtonDirective,
    GeneralGridComponent,
    Dialog,
    FormsModule,
    ReactiveFormsModule,
    InputText,
    Checkbox,
    PickList,
    PrimeTemplate,
    MultiSelect,
    TranslatePipe,
  ],
})
export class NoteTypesContainerComponent
  extends SearchContainerComponent<NoteType>
  implements OnInit, OnDestroy
{
  protected _noteTypeApi = inject(NoteTypeApiService);
  protected _adminBuilder = inject(AdminBuilderService);
  protected _translateService = inject(TranslateService);
  protected _logAndMessage = inject(LogAndMessageService);
  protected _tabService = inject(TabService);
  protected _noteTypeTableService = inject(NoteTypeTableService);
  protected _fb = inject(UntypedFormBuilder);
  protected _confirmationService = inject(ConfirmationService);
  protected _authApi = inject(AuthApiService);
  protected _adminApi = inject(AdminApiService);
  protected _auth = inject(AuthService);
  protected _aorApi = inject(AorApiService);

  readonly businessUnitFilter =
    viewChild<BusinessUnitFilterComponent>('buFilter');
  canCreate = false;
  canEdit = false;
  canDelete = false;
  saving = false;
  form!: UntypedFormGroup;
  displayDialog = false;
  dialogHeader!: string;

  types: NoteType[] = [];
  categories: CategoryObject[] = [];
  selectedType?: NoteType;
  showSuccessMessage = true;
  sub!: Subscription;

  activeTabChangedSub!: Subscription;

  availableAORs: AOR[] = [];
  sourcePicklist: AOR[] = [];
  targetPicklist: AOR[] = [];

  constructor() {
    super();
  }

  ngOnInit() {
    this._noteTypeTableService.setLastLazyLoad(null);
    this.setTableService(this._noteTypeTableService);
    this.setupActiveTabChangedSubscription();
    this.form = this._fb.group({
      id: [null],
      name: [{ value: null, disabled: false }, Validators.required],
      enabled: [{ value: false, disabled: false }],
      version: [null],
      categories: [{ value: null, disabled: false }, Validators.required],
      aors: [{ value: null, disabled: false }],
    });

    this.columns = NOTE_TYPES_TABLE_DEFINITION;

    const screenName$ = this._translateService.get('CRM.SCREEN.NOTE_TYPES');
    const dialogHeader$ = this._translateService.get(
      'CRM.LABEL.NOTE_TYPE_DETAILS'
    );
    forkJoin([screenName$, dialogHeader$]).subscribe((label) => {
      this._tabService.updateActiveTabLabel(label[0]);
      this.screenName = label[0];
      this.dialogHeader = label[1];
    });

    this._aorApi.getAORs(null).subscribe((aors) => {
      this.availableAORs = aors;
    });

    this._noteTypeApi
      .getControllerNoteCategories()
      .subscribe((categories) => (this.categories = categories));

    this.canCreate = this._authApi.doesUserHaveAllClaimsFromList([
      CLAIMS.ADMINISTRATION.NOTE_TYPES.CREATE_NOTE_TYPES,
    ]);
    this.canEdit = this._authApi.doesUserHaveAllClaimsFromList([
      CLAIMS.ADMINISTRATION.NOTE_TYPES.EDIT_NOTE_TYPES,
    ]);
    this.canDelete = this._authApi.doesUserHaveAllClaimsFromList([
      CLAIMS.ADMINISTRATION.NOTE_TYPES.DELETE_NOTE_TYPES,
    ]);

    this.form.disable();

    this.applyPreviousData(); // check into what these do
    this.applyPreviousFilters();
  }

  ngOnDestroy(): void {
    if (this.activeTabChangedSub) {
      this.activeTabChangedSub.unsubscribe();
    }
  }

  override handleLazyLoad(
    req: PageRequest,
    $event: GridLazyLoadEvent,
    filters: LazyLoadFilters
  ) {
    this._noteTypeTableService.setLastLazyLoad($event.lazy);
    const query: NoteTypeSortOrQuery = {
      name: null,
      categoryString: null,
      active: null,
      businessUnits: null,
      aorString: null,
    };

    let selectedBusinessUnits = [];
    const businessUnitFilter = this.businessUnitFilter();
    if (businessUnitFilter?.selectedBusinessUnits) {
      // Handle further refreshes & changes to business unit filter component
      selectedBusinessUnits = businessUnitFilter.selectedBusinessUnits;
    } else {
      // Handle initial page load (before the business unit filter subcomponent exists)
      selectedBusinessUnits = this._authApi
        .getUserBusinessUnits()
        .map((bu) => bu.id);
    }
    query.businessUnits = selectedBusinessUnits;
    if (filters.name) {
      query.name = (filters.name as FilterMetadata).value;
    }

    if (filters.active) {
      query.active = (filters.active as FilterMetadata).value.toString();
    }

    if (filters.categoryString) {
      query.categoryString = (filters.categoryString as FilterMetadata).value;
    }

    if (filters.aorString) {
      query.aorString = (filters.aorString as FilterMetadata).value;
    }

    this.queryNetwork(req, $event, query);
  }

  handleBusinessUnitChange() {
    this.lazyLoad({ lazy: this._noteTypeTableService.getLastLazyLoad()! });
  }

  queryNetwork(
    req: PageRequest,
    $event: GridLazyLoadEvent,
    query: NoteTypeSortOrQuery
  ): void {
    req.page += 1;
    const sort = this.buildNetworkSortQuery($event, 'name');
    this.loading = true;
    this._noteTypeApi
      .queryForNoteTypes(req.page, req.pageSize, query, sort)
      .subscribe({
        next: (response) => {
          this.types = response.items;
          this.totalRecords = response.totalRecords;
          this.elements = [...this.types];
          this.loading = false;
        },
        error: (error) => {
          this.loading = false;
          this._logAndMessage.errorLogOnly(error);
          this._logAndMessage.translateToErrorAlert({
            bodyKey: 'CRM.MESSAGES.ERROR.QUERY_NOTE_TYPES',
            headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
          });
        },
      });
  }

  changeViewEvent($event: { data: NoteType }): void {
    this.selectedType = $event.data;
    this.form.patchValue({
      id: $event.data.id,
      name: $event.data.name,
      enabled: $event.data.active,
      version: $event.data.version,
      categories: this.categories.filter((cat) =>
        $event.data.categories.some((cat2) => cat.id === cat2.id)
      ),
      aors: $event.data.aors,
    });

    const aors = this.form.controls['aors'].value;
    let currentAors: string[] = [];
    aors.forEach((x: AOR) => {
      currentAors.push(x.name);
    });

    this.sourcePicklist = [];
    this.targetPicklist = [];
    this.availableAORs.forEach((x) => {
      if (currentAors.includes(x.name)) {
        this.targetPicklist.push(x);
      } else {
        this.sourcePicklist.push(x);
      }
    });
    this.sourcePicklist.sort((a, b) => sortByProperty(a, b, 'name'));
    this.targetPicklist.sort((a, b) => sortByProperty(a, b, 'name'));

    if (this.canEdit) {
      this.form.enable();
    } else {
      this.form.disable();
    }
    this.displayDialog = true;
  }

  clearForm(): void {
    this.form.reset();
  }

  dialogClosed(): void {
    this.clearForm();
    this.selectedType = undefined;
  }

  newNoteType(): void {
    this.form.reset();
    this.form.controls['enabled'].patchValue(false, { emitEvent: true });
    this.sourcePicklist = [];
    this.targetPicklist = [];
    this.availableAORs.forEach((x) => {
      this.sourcePicklist.push(x);
    });
    this.sourcePicklist.sort((a, b) => sortByProperty(a, b, 'name'));
    this.displayDialog = true;
    if (this.canCreate) {
      this.form.enable();
    } else {
      this.form.disable();
    }
  }

  refresh(): void {
    this.lazyLoad({ lazy: this._noteTypeTableService.getLastLazyLoad()! });
  }

  nameChanged(): void {
    if (this.selectedType) {
      this.selectedType.name = this.form.controls['name'].value;
    }
  }

  saveNoteType(): void {
    if (!(this.canCreate || this.canEdit)) {
      return;
    }
    if (!isNullOrUndefined(this.form) && this.form.valid) {
      let type = null;
      if (this.selectedType) {
        // Handle edits
        type = this.selectedType;
        type.updater = this._auth.getUserName();
        let formObject = Object.assign({}, this.form.getRawValue());
        type.name = formObject.name;
        type.categories = formObject.categories.map((cat: any) => cat.id);
        type.active = formObject.enabled;
        type.aors = this.targetPicklist;
      } else {
        // Handle new creations
        type = Object.assign({}, this.form.getRawValue());
        type.createdBy = this._auth.getUserName();
        type.categories = type.categories.map((cat: any) => cat.id);
        type.active = type.enabled;
      }
      if (!type.id) {
        this.createNoteType(type);
      } else {
        this.updateNoteType(type, true);
      }
    }
  }

  createNoteType(noteType: NoteType) {
    if (!this.canCreate) {
      this._logAndMessage.error(
        'Cannot create note type!',
        'Logged in user does not have appropriate claim.'
      );
      return;
    }

    const noteTypeInput: NoteTypeInput | null =
      this._adminBuilder.buildNoteTypeInput(
        noteType,
        true,
        this.targetPicklist
      );
    const isValid = isValidNoteTypeInput(noteTypeInput, this.types);
    if (!isNullOrUndefined(isValid)) {
      this._logAndMessage.translateToErrorAlert({
        headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
        bodyKey: isValid,
      });
      return;
    }
    this.saving = true;
    let potentialNoteType: NoteType | undefined;
    this._noteTypeApi
      .createNoteType(noteTypeInput)
      .pipe(take(1))
      .subscribe({
        next: (result: NoteType) => {
          this.clearForm();
          this.displayDialog = false;
          potentialNoteType = result;
        },
        error: (error) => {
          this.saving = false;
          this._logAndMessage.errorLogOnly(error);
          this._logAndMessage.translateToErrorAlert({
            bodyKey: 'CRM.MESSAGES.ERROR.CREATE_NOTE_TYPE',
            bodyParams: { name: noteType.name },
            headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
          });
        },
        complete: () => {
          this.saving = false;
          this._logAndMessage.translateToSuccessMessage({
            bodyKey: 'CRM.MESSAGES.SUCCESS.CREATE_NOTE_TYPE',
            bodyParams: { name: noteType.name },
            headerKey: 'COMMON.MESSAGES.HEADERS.SUCCESS',
          });
          this.types.push(potentialNoteType!);
          this.totalRecords += 1;
          this.refresh();
        },
      });
  }

  updateNoteType(changes: NoteType, closeDialog: boolean) {
    if (!this.canEdit) {
      this._logAndMessage.error(
        'Cannot update note type!',
        'Logged in user does not have appropriate claim.'
      );
      return;
    }

    const typeChanges: NoteTypeInput | null =
      this._adminBuilder.buildNoteTypeInput(
        changes,
        false,
        this.targetPicklist
      );

    this.saving = true;
    this._noteTypeApi
      .updateNoteType(changes.id, typeChanges)
      .pipe(take(1))
      .subscribe({
        next: (result) => {
          this.clearForm();
          this.displayDialog = false;
        },
        error: (error) => {
          this.saving = false;
          this._logAndMessage.errorLogOnly(error);
          this._logAndMessage.translateToErrorAlert({
            bodyKey: 'CRM.MESSAGES.ERROR.UPDATE_NOTE_TYPE',
            bodyParams: { name: changes.name },
            headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
          });
        },
        complete: () => {
          this.saving = false;
          this._logAndMessage.translateToSuccessMessage({
            bodyKey: 'CRM.MESSAGES.SUCCESS.UPDATE_NOTE_TYPE',
            bodyParams: { name: changes.name },
            headerKey: 'COMMON.MESSAGES.HEADERS.SUCCESS',
          });
          this.refresh();
        },
      });
  }

  deleteNoteType(): void {
    if (!this.canDelete) {
      this._logAndMessage.error(
        'Cannot delete note type!',
        'Logged in user does not have appropriate claim.'
      );
      return;
    }

    if (!isNullOrUndefined(this.form)) {
      const noteType = Object.assign({}, this.form.getRawValue());
      let confHeader = null,
        ok = null,
        cancel = null,
        message = null;
      const confHeader$ = this._translateService.get(
        'CRM.MESSAGES.HEADERS.DELETE_NOTE_TYPE'
      );
      const ok$ = this._translateService.get('COMMON.LABEL.BUTTONS.YES');
      const cancel$ = this._translateService.get('COMMON.LABEL.BUTTONS.NO');
      const message$ = this._translateService.get(
        'CRM.MESSAGES.CONFIRMATION.DELETE_NOTE_TYPE'
      );

      forkJoin([confHeader$, ok$, cancel$, message$]).subscribe((messages) => {
        confHeader = messages[0];
        ok = messages[1];
        cancel = messages[2];
        message = messages[3];
      });

      this._confirmationService.confirm({
        header: confHeader!,
        icon: 'fa fa-question-circle',
        acceptVisible: true,
        rejectVisible: true,
        acceptLabel: ok!,
        rejectLabel: cancel!,
        message: message!,
        accept: () => {
          this.saving = true;
          if (!isNullOrUndefined(noteType.id)) {
            this._noteTypeApi
              .allowNoteTypeDelete(noteType.id)
              .subscribe((canDelete) => {
                const allowed = canDelete;
                if (!allowed) {
                  this.saving = false;
                  this._logAndMessage.translateToErrorAlert({
                    bodyKey: 'ADMIN.MESSAGES.ERROR.DELETE_NOTE_TYPE',
                    headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
                  });
                } else {
                  this._noteTypeApi.deleteNoteType(noteType.id).subscribe({
                    next: (result) => {
                      this.saving = false;
                      this.clearForm();
                      this.displayDialog = false;
                      this.refresh();
                    },
                    error: (error) => {
                      this._logAndMessage.errorLogOnly(error);
                      this._logAndMessage.translateToErrorAlert({
                        bodyKey: 'CRM.MESSAGES.ERROR.DELETE_NOTE_TYPE',
                        bodyParams: { name: noteType.name },
                        headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
                      });
                    },
                    complete: () => {
                      this._logAndMessage.translateToSuccessMessage({
                        bodyKey: 'CRM.MESSAGES.SUCCESS.DELETE_NOTE_TYPE',
                        bodyParams: { name: noteType.name },
                        headerKey: 'COMMON.MESSAGES.HEADERS.SUCCESS',
                      });
                    },
                  });
                }
              });
          } else {
            this.displayDialog = false;
          }
        },
        reject: () => {},
      });
    }
  }

  setupActiveTabChangedSubscription(): void {
    this.activeTabChangedSub = this._tabService.activeTabChanged.subscribe(
      (tab) => {
        if (!isNullOrUndefined(tab) && tab.header === this.screenName) {
          this.refresh();
        }
      }
    );
  }
}
